import {OnInit, ViewChild} from "@angular/core";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {StateService} from "@tva/ng-migration/core/services/state/state.service";
import {UnsavedChangesService} from "@tva/ng-migration/shared/services/unsavedChanges.service";
import {NotificationService, TvActionChipsComponent} from "@tva/ng-migration/uicomponents";
import {debounceTime, distinctUntilChanged, filter, startWith} from "rxjs/operators";
import {CourseManagementConstants} from "../components/CourseManagement.constants";
import {CourseManagementService} from "../services/course-management.service";
import {
  CurricRequiredValidator,
  DaysPerWeekRangeValidator,
  DefaultAttendanceMinutesRangeValidator,
  MaxNumberPrepDaysRangeValidator
} from "../validators/course-management.validators";
import {CourseManagement} from "./course-management";

export class CourseManagementEditableBaseComponent implements OnInit {

  form: FormGroup;
  coursesList = [];
  statusList = [
    { id: 1, label: 'Active' },
    { id: 0, label: 'Inactive' },
  ];
  lists = {
    courseSubjectList: [],
    courseContentTypeList: [],
    courseTypeList: [],
    courseLengthList: [],
    defaultCredits: [],
    gradeLevelList: [],
    platformTypes: [],
    courseVendorList: [],
    contentProviderCode: [],
    stateList: [],
  };
  isLoading = true;
  wasValidated: boolean;
  promptAsking: Function;

  @ViewChild('courseAutocomplete') courseAutocomplete: TvActionChipsComponent;
  @ViewChild(TvActionChipsComponent) courseList: TvActionChipsComponent;

  get wasModified(): boolean {
    return this.form.dirty;
  }

  get errorMessages() {
    return CourseManagementConstants.errors;
  }

  constructor(protected courseManagementService: CourseManagementService,
    protected state: StateService, protected notification: NotificationService, protected unsavedChanges: UnsavedChangesService) { }

  ngOnInit(): void {
    this.getLists();
    this.initializeForm();
  }

  ngAfterViewInit(): void {
    this.setCoursesList();
  }

  ngOnDestroy(): void {
    this.unsavedChanges.resetMessage();
    this.promptAsking();
  }

  public cancel(): void {
    this.state.go('courseManagement');
  }

  formErrorsAreShown(): boolean {
    return this.wasValidated && this.form.touched;
  }

  modifiesNextCourse(): void {
    this.coursesList = [];
  }

  private getLists(): void {
    this.courseManagementService.getCourseDropDownDetails().subscribe((data) => {
      this.lists = data;
      this.afterListLoaded();
    });
  };
  
  protected afterListLoaded(): void{
    this.isLoading = false;
  }

  private initializeForm(): void {
    this.form = new FormGroup({
      courseCode: new FormControl('', [Validators.required]),
      courseName: new FormControl('', [Validators.required]),
      courseSubject: new FormControl(),
      courseContentType: new FormControl(),
      abbreviation: new FormControl(''),
      courseTypeObject: new FormControl('', [Validators.required]),
      courseLevel: new FormControl(),
      numDaysWeekIdeal: new FormControl('', [CurricRequiredValidator, DaysPerWeekRangeValidator]),
      courseLength: new FormControl(),
      defaultAttendanceMins: new FormControl('', [CurricRequiredValidator, DefaultAttendanceMinutesRangeValidator]),
      defaultCredits: new FormControl('', [Validators.required]),
      maxAdvPrepDays: new FormControl('', [CurricRequiredValidator, MaxNumberPrepDaysRangeValidator]),
      courseGrade: new FormControl('', [Validators.required]),
      platformId: new FormControl('', [CurricRequiredValidator]),
      courseVendors: new FormControl([]),
      contentProviderCode: new FormControl('', [CurricRequiredValidator]),
      nextCourses: new FormControl([]),
      status: new FormControl(1),
      specifiedStates: new FormControl([]),
    });

    this.form.get('courseContentType').valueChanges.subscribe((courseContentType) => {
      if (CourseManagement.isAttendanceOnly({ courseContentType })) {
        this.setDefaultFormValues();
      }
    });

    this.form.get('courseTypeObject').valueChanges.subscribe((courseTypeObject) => {
      if (CourseManagement.isD2LCourse({ courseTypeObject })) {
        this.setDefaultFormValues();
      }
    })
  }

  protected setModalOnLeave() {
    this.unsavedChanges.setMessage(CourseManagementConstants.unsavedMessage);
    this.form.valueChanges.subscribe(() => {
      this.unsavedChanges.startChanges();
    })
    this.promptAsking = this.unsavedChanges.setModalOnLeave();
  }


  private setCoursesList(): void {
    this.courseAutocomplete.searchCtrl.valueChanges
      .pipe(
        startWith(''),
        debounceTime(400),
        distinctUntilChanged(),
        filter(term => term?.length > 2)
      )
      .subscribe(val => {
        this.courseManagementService.nextCourseListByCode(val || '')
          .subscribe(response => {
            this.coursesList = response;
            this.courseAutocomplete.filteredItems = response.filter(item => !this.form.get('nextCourses').value.some(course => course.nextCourseId === item.nextCourseId));
          })

      });
  }

  private setDefaultFormValues(): void {
    if (!this.form.value.numDaysWeekIdeal)
      this.form.get('numDaysWeekIdeal').setValue(5);
    if (!this.form.value.defaultAttendanceMins)
      this.form.get('defaultAttendanceMins').setValue(60);
    if (!this.form.value.maxAdvPrepDays)
      this.form.get('maxAdvPrepDays').setValue(1);
    if (!this.form.value.platformId)
      this.form.get('platformId').setValue(5);
    if (!this.form.value.contentProviderCode)
      this.form.get('contentProviderCode').setValue(2);
  }


  protected validate(): boolean {
    this.form.markAsTouched();
    this.courseList.validate();
    this.wasValidated = true;
    return this.form.valid;
  }

  protected getCourseData(): any {
    const course = {};
    Object.keys(this.form.value).forEach(key => {
      if (this.form.value[key] || this.form.value[key] === 0)
        course[key] = this.form.value[key]
    })
    return course;
  }
}
